import './DialogueSettingsModal.pcss';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import { Label } from 'reactstrap';
import { ModalInstanceProps } from '../interfaces';
import MenuButtonListEditor from '../../../editor/features/build/components/widgets/MenuButtonListEditor/MenuButtonListEditor';
import { ModalBody, ModalHeader } from '../modal-helpers';
import { MAX_ALLOWED_MENU_BTNS } from '@mm/constants/widgets';
import { useMainMenuCRUDActions } from '@mm/state/actions/MainMenuCRUDActions';
import { MainMenuItemModel } from '@mm/state/model/MainMenuModel';
import { mainMenuButtonsSelector } from '@mm/state/selectors/mainMenuSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import * as routes from '../../constants/routes';

type OuterProps = {
  pageId: number;
  skipMenuLoad?: boolean;
};

type Props = ModalInstanceProps & OuterProps;

export const DialogueSettingsModal = (props: Props) => {
  const { pageId, closeModal, skipMenuLoad = false } = props;
  const modalActions = useModalActions();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const { history } = useRouter();
  const buttons = useSelector(mainMenuButtonsSelector);
  const mainMenuCRUDActions = useMainMenuCRUDActions();
  const pageCRUDActions = usePageCRUDActions();
  const page = useSelector((state: StoreState) =>
    pageEntityByIdSelector(state, pageId.toString()),
  );
  const onCreateButton = useCallback(
    (chatbotId: number, menuItem: Omit<MainMenuItemModel, 'id'>) =>
      mainMenuCRUDActions.create(chatbotId, { ...menuItem, pageId }),
    [pageId],
  );
  const getMenuItems = useCallback(
    (chatbotId: number) => mainMenuCRUDActions.retrieveAll(chatbotId, pageId),
    [pageId],
  );
  const onDeleteButton = useCallback(
    (chatbotId: number, menuItem: MainMenuItemModel) =>
      mainMenuCRUDActions.delete(chatbotId, menuItem),
    [],
  );
  const onUpdateButton = useCallback(
    (chatbotId: number, menuItem: MainMenuItemModel) =>
      mainMenuCRUDActions.update(chatbotId, menuItem),
    [],
  );
  const customMenuSelected = page?.useCustomMenu;
  const redirectToPageMenu = () => {
    closeModal();
    history.push(
      `${routes.REFRESH}${routes.CHATBOT_EDITOR}/${chatbotId}/build/main-menu`,
    );
  };
  const onSelectPageMenu = () => {
    if (page.useCustomMenu) {
      pageCRUDActions.update({ ...page, useCustomMenu: false });
    }
  };
  const onSelectCustomMenu = () => {
    if (!page.useCustomMenu) {
      pageCRUDActions.update({ ...page, useCustomMenu: true });
    }
  };

  useEffect(() => {
    if (!page) {
      pageCRUDActions.retrieveById(pageId);
    }
  }, [pageId]);

  useEffect(() => {
    if (!skipMenuLoad) {
      getMenuItems(chatbotId);
    }
  }, [skipMenuLoad]);

  const editorProps = {
    pageId,
    buttons,
    closeModalOverride: () =>
      modalActions.openDialogueSettingsModal(pageId, true),
    onCreateButton,
    onUpdateButton,
    onDeleteButton,
    maxButtonCount: MAX_ALLOWED_MENU_BTNS,
  };

  return (
    <div className="dialogue-settings-modal">
      <ModalHeader className="dialogue-settings-modal__header" />
      <ModalBody className="dialogue-settings-modal__body">
        <div className="dialogue-settings-modal__settings-title">
          Menu Settings:
        </div>
        <div className="dialogue-settings-modal__radio-column">
          <div className="dialogue-settings-modal__radio-container">
            <input
              type="radio"
              id="fb-page-menu-checker"
              className="dialogue-settings-modal__radio-input"
              checked={!customMenuSelected}
              onChange={onSelectPageMenu}
            />
            <Label
              className="dialogue-settings-modal__radio-label"
              for="fb-page-menu-checker"
            >
              Use page level menu.
            </Label>
            <span
              className="dialogue-settings-modal__page-menu-redirect"
              onClick={redirectToPageMenu}
            >
              View Menu
            </span>
          </div>
          <div className="dialogue-settings-modal__radio-container">
            <input
              type="radio"
              id="custom-menu-checker"
              className="dialogue-settings-modal__radio-input"
              checked={customMenuSelected}
              onChange={onSelectCustomMenu}
            />
            <Label
              className="dialogue-settings-modal__radio-label"
              for="custom-menu-checker"
            >
              Set a specific menu for this Dialogue.
            </Label>
          </div>
        </div>
        {customMenuSelected && (
          <div className="dialogue-settings-modal__settings-container">
            <MenuButtonListEditor {...editorProps} />
          </div>
        )}
      </ModalBody>
    </div>
  );
};
